/**
 * Dashboard-view data layer: pure aggregation helpers over the board's rows.
 *
 * Everything here is client-side — the Dashboard view visualizes the SAME rows
 * the Table/Kanban/Timeline views already have in memory, so widgets recompute
 * instantly and respect the toolbar's search/filters for free. The one network
 * concern (the AI suggestion) only ever receives the compact profile built by
 * `buildBoardProfile` — column metadata + value distributions, never row text.
 */
import { v4 as uuidv4 } from 'uuid';
import type { DashboardWidget, SuggestDashboardRequestDto } from '@/entities/tracker';
import { i18n } from '@/shared/i18n';

import { parseLocalDate } from './parseLocalDate';
import type { CustomColumn } from './useTrackersBoard';

/** Source-agnostic row shape (mirrors the Kanban/Timeline adapters). */
export interface DashboardItem {
  id: string;
  title: string;
  values?: Record<string, unknown> | null;
  /** Row creation time — powers the default "over time" line. */
  createdAt?: string;
}

export interface CategoryDatum {
  /** Stable bucket key (status option id, user id, 'true'/'false', …). */
  key: string;
  /** Human label when the lib can resolve it; components may re-map (people). */
  label: string;
  /** Color when the column defines one (status options). */
  color?: string;
  count: number;
}

export interface TimeSeriesDatum {
  /** Sortable bucket key (e.g. '2026-07' or '2026-07-06'). */
  key: string;
  label: string;
  count: number;
}

// eslint-disable-next-line i18next/no-literal-string -- bucket id sentinel
export const NO_VALUE_KEY = '__none__';
export const NO_VALUE_COLOR = '#94a3b8';

// ── The dashboard's "invisible grid": 12 columns × fixed-height rows ─────────
export const GRID_COLS = 12;
/** Pixel height of one grid row. */
export const GRID_ROW_HEIGHT = 64;
/** Gap between cells (matches Tailwind `gap-4`). */
export const GRID_GAP = 16;
export const WIDGET_MIN_W = 2;
export const WIDGET_MIN_H = 2;

/** Legacy `size` → grid geometry, for widgets created before free-resize. */
const SIZE_TO_WH: Record<string, { w: number; h: number }> = {
  sm: { w: 3, h: 2 },
  md: { w: 6, h: 4 },
  lg: { w: 12, h: 4 },
};

/**
 * Resolve a widget's grid geometry: explicit `w`/`h` when set (free-resize),
 * else a default derived from the legacy `size`. Clamped to grid bounds.
 */
export function widgetGeom(widget: { w?: number; h?: number; size?: string }): {
  w: number;
  h: number;
} {
  // eslint-disable-next-line i18next/no-literal-string -- size lookup key
  const fallback = SIZE_TO_WH[widget.size ?? 'md'] ?? SIZE_TO_WH.md;
  return {
    w: Math.min(GRID_COLS, Math.max(WIDGET_MIN_W, widget.w ?? fallback.w)),
    h: Math.max(WIDGET_MIN_H, widget.h ?? fallback.h),
  };
}

const rawValue = (item: DashboardItem, columnId: string): unknown =>
  (item.values ?? undefined)?.[columnId];

/**
 * Bucket rows by a categorical column (status / people / checkbox /
 * brief_type). People columns count an item once per assignee. Rows without a
 * value land in a trailing "No value" bucket (only emitted when non-empty).
 */
export function countByColumn(items: DashboardItem[], column: CustomColumn): CategoryDatum[] {
  const counts = new Map<string, number>();
  let none = 0;
  const bump = (key: string) => counts.set(key, (counts.get(key) ?? 0) + 1);

  for (const item of items) {
    const value = rawValue(item, column.id);
    if (column.type === 'people') {
      const ids = Array.isArray(value)
        ? value.filter((v): v is string => typeof v === 'string')
        : [];
      if (ids.length === 0) none += 1;
      else ids.forEach(bump);
    } else if (column.type === 'checkbox') {
      // eslint-disable-next-line i18next/no-literal-string -- stringified boolean used as the bucket key
      bump(value === true ? 'true' : 'false');
    } else if (typeof value === 'string' && value !== '') {
      bump(value);
    } else {
      none += 1;
    }
  }

  const optionById = new Map((column.statusOptions ?? []).map((o) => [o.id, o]));
  const data: CategoryDatum[] = [];

  if (column.type === 'status' && column.statusOptions?.length) {
    // Preserve the configured status order; keep any stray values after.
    for (const option of column.statusOptions) {
      const count = counts.get(option.id) ?? 0;
      if (count > 0) data.push({ key: option.id, label: option.label, color: option.color, count });
      counts.delete(option.id);
    }
  }
  for (const [key, count] of counts) {
    const option = optionById.get(key);
    const label =
      column.type === 'checkbox'
        ? key === 'true'
          ? i18n.t('trackers:trackersBoard.checkboxCustomCell.checked')
          : i18n.t('trackers:trackersBoard.checkboxCustomCell.unchecked')
        : (option?.label ?? key);
    data.push({ key, label, color: option?.color, count });
  }
  if (none > 0)
    data.push({
      key: NO_VALUE_KEY,
      label: i18n.t('trackers:trackersBoard.dashboard.noValue'),
      color: NO_VALUE_COLOR,
      count: none,
    });
  return data;
}

export interface NumberStats {
  sum: number;
  avg: number;
  filled: number;
}

export function numberStats(items: DashboardItem[], columnId: string): NumberStats {
  let sum = 0;
  let filled = 0;
  for (const item of items) {
    const value = rawValue(item, columnId);
    if (typeof value === 'number' && Number.isFinite(value)) {
      sum += value;
      filled += 1;
    }
  }
  return { sum, avg: filled > 0 ? sum / filled : 0, filled };
}

/** Extract the date string a row contributes to the time series. */
function dateOf(item: DashboardItem, column: CustomColumn | null): string | undefined {
  if (!column) return item.createdAt;
  const value = rawValue(item, column.id);
  if (typeof value === 'string') return value;
  // date_range stores { start?, end? } — use the start.
  if (value && typeof value === 'object' && 'start' in value) {
    const start = (value as { start?: unknown }).start;
    return typeof start === 'string' ? start : undefined;
  }
  return undefined;
}

/** Monday-based start of week, as a local date. */
function startOfWeek(d: Date): Date {
  const day = (d.getDay() + 6) % 7;
  const out = new Date(d);
  out.setDate(d.getDate() - day);
  return out;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Count rows per week/month bucket of a date column (or created-at when
 * `column` is null). Buckets are sorted ascending; rows without a parsable
 * date are skipped.
 */
export function timeSeries(
  items: DashboardItem[],
  column: CustomColumn | null,
  bucket: 'week' | 'month',
): TimeSeriesDatum[] {
  const buckets = new Map<string, { label: string; count: number }>();
  for (const item of items) {
    const raw = dateOf(item, column);
    if (!raw) continue;
    const date = parseLocalDate(raw);
    if (!date) continue;
    let key: string;
    let label: string;
    if (bucket === 'month') {
      key = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
      label = date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    } else {
      const start = startOfWeek(date);
      key = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      label = start.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }
    const entry = buckets.get(key);
    if (entry) entry.count += 1;
    else buckets.set(key, { label, count: 1 });
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, { label, count }]) => ({ key, label, count }));
}

// ─── AI board profile ────────────────────────────────────────────────────────

/** Column types the profile carries a distribution for. */
// eslint-disable-next-line i18next/no-literal-string -- column type discriminators
const CATEGORICAL_TYPES = new Set(['status', 'people', 'brief_type', 'checkbox']);

/**
 * Compact, privacy-lean profile of the board for the AI dashboard designer:
 * column metadata + value distributions and fill counts — no row contents.
 */
export function buildBoardProfile(
  items: DashboardItem[],
  columns: CustomColumn[],
): SuggestDashboardRequestDto {
  return {
    rowCount: items.length,
    columns: columns.map((column) => {
      const filledCount = items.filter((i) => {
        const v = rawValue(i, column.id);
        return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '';
      }).length;
      const categorical = CATEGORICAL_TYPES.has(column.type);
      return {
        id: column.id,
        label: column.label,
        type: column.type,
        filledCount,
        ...(column.statusOptions?.length
          ? { options: column.statusOptions.map((o) => ({ id: o.id, label: o.label })) }
          : {}),
        ...(categorical
          ? {
              distribution: Object.fromEntries(
                countByColumn(items, column).map((d) => [d.key, d.count]),
              ),
            }
          : {}),
      };
    }),
  };
}

// ─── Deterministic starter dashboard (AI fallback) ───────────────────────────

/**
 * Heuristic starter dashboard used when the AI suggestion is unavailable
 * (no LLM key, offline, empty response): total-count KPI, battery + donut for
 * the first status column, a people bar, a number-sum KPI, and a created-over-
 * time line. Always valid, always instant.
 */
export function buildDefaultDashboard(columns: CustomColumn[]): DashboardWidget[] {
  const widgets: DashboardWidget[] = [];
  const status = columns.find((c) => c.type === 'status' && c.statusOptions?.length);
  const people = columns.find((c) => c.type === 'people');
  const number = columns.find((c) => c.type === 'number');

  widgets.push({
    id: uuidv4(),
    type: 'kpi',
    title: i18n.t('trackers:trackersBoard.dashboard.totalItems'),
    size: 'sm',
    config: { agg: 'count' },
  });
  if (number) {
    widgets.push({
      id: uuidv4(),
      type: 'kpi',
      title: i18n.t('trackers:trackersBoard.dashboard.totalColumn', {
        column: number.label.toLowerCase(),
      }),
      size: 'sm',
      config: { columnId: number.id, agg: 'sum' },
    });
  }
  if (status) {
    widgets.push({
      id: uuidv4(),
      type: 'battery',
      title: i18n.t('trackers:trackersBoard.dashboard.columnProgress', { column: status.label }),
      size: 'md',
      config: { columnId: status.id },
    });
    widgets.push({
      id: uuidv4(),
      type: 'donut',
      title: i18n.t('trackers:trackersBoard.dashboard.itemsByColumn', {
        column: status.label.toLowerCase(),
      }),
      size: 'md',
      config: { columnId: status.id },
    });
  }
  if (people) {
    widgets.push({
      id: uuidv4(),
      type: 'bar',
      title: i18n.t('trackers:trackersBoard.dashboard.itemsByColumn', {
        column: people.label.toLowerCase(),
      }),
      size: 'md',
      config: { columnId: people.id },
    });
  }
  widgets.push({
    id: uuidv4(),
    type: 'line',
    title: i18n.t('trackers:trackersBoard.dashboard.itemsCreatedOverTime'),
    size: widgets.length % 2 === 0 ? 'lg' : 'md',
    config: { dateBucket: 'week' },
  });
  return widgets;
}
